// Slice 4: Review Room page.
//
// Every claim about the company that Meera, an Advisor, or admin makes
// lands here as a row. Founder accepts, corrects, or rejects each one.
// Admin can override and publish a claim to the investor view without
// founder action (PC's rule: the first investor view was built by admin
// only). Status admin_published is shown distinctly so the founder always
// knows what the investor sees.
import { useEffect, useState } from "react";
import { Check, X, Edit3, ShieldCheck, FileText, AlertTriangle, RefreshCw } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

type ClaimStatus = "pending" | "accepted" | "corrected" | "rejected" | "admin_published";

interface ClaimReview {
  id: number;
  source: string;
  sourceLabel: string;
  field: string;
  fieldLabel: string;
  proposedValue: string;
  status: ClaimStatus;
  correctedValue: string | null;
  rejectReason: string | null;
  respondedAt: string | null;
  adminOverrideBy: string | null;
  adminOverrideAt: string | null;
  createdAt: string;
}

function authHeaders(): Record<string, string> {
  return { "x-founder-token": localStorage.getItem("founder_token") || "" };
}

export default function FounderReviewRoom() {
  const [claims, setClaims] = useState<ClaimReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"pending" | "all">("pending");

  async function load() {
    try {
      const res = await fetch(`${BASE}api/founder-portal/review-claims`, { headers: authHeaders() });
      if (!res.ok) {
        setError(res.status === 401 ? "Please sign in again." : "Could not load Review Room.");
        return;
      }
      const data = await res.json();
      setClaims(data.claims);
    } catch {
      setError("Could not load Review Room.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function respond(claimId: number, action: "accept" | "correct" | "reject", payload: any = {}) {
    const res = await fetch(`${BASE}api/founder-portal/review-claims/${claimId}/respond`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ action, ...payload }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j?.error || "Update failed");
    }
    await load();
  }

  if (loading) return <div className="p-12 text-white/60 text-sm">Loading.</div>;
  if (error) return <div className="p-12 text-red-400 text-sm">{error}</div>;

  const pendingCount = claims.filter((c) => c.status === "pending").length;
  const visible = filter === "pending" ? claims.filter((c) => c.status === "pending") : claims;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mb-2">
          Review Room
        </div>
        <h1 className="text-2xl font-bold tracking-tight mb-2">What is being said about your company</h1>
        <p className="text-sm text-white/65 leading-relaxed max-w-2xl">
          Every claim Meera, your Advisor, or admin makes lands here. Accept it, correct it, or reject it.
          Anything that becomes part of your investor profile shows it here.
        </p>
      </div>

      {/* Counter + filter */}
      <div className="flex items-center justify-between mb-4 bg-black/30 border border-white/[0.08] rounded-lg px-4 py-3">
        <div className="text-sm">
          <span className="font-bold text-[#FF5C00]">{pendingCount}</span>
          <span className="text-white/60"> {pendingCount === 1 ? "claim" : "claims"} waiting for your review</span>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <FilterBtn active={filter === "pending"} onClick={() => setFilter("pending")}>Pending</FilterBtn>
          <FilterBtn active={filter === "all"} onClick={() => setFilter("all")}>All ({claims.length})</FilterBtn>
        </div>
      </div>

      {/* Claims list */}
      {visible.length === 0 ? (
        <div className="bg-black/30 border border-white/[0.08] rounded-xl p-10 text-center">
          <ShieldCheck className="w-8 h-8 text-white/60 mx-auto mb-3" />
          <p className="text-sm text-white/60">
            {filter === "pending" ? "Nothing pending right now." : "No claims yet."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((c) => <ClaimCard key={c.id} claim={c} onRespond={respond} />)}
        </div>
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded font-medium ${active ? "bg-[#FF5C00]/15 text-[#FF5C00]" : "text-white/60 hover:text-white"}`}
    >
      {children}
    </button>
  );
}

function ClaimCard({ claim, onRespond }: {
  claim: ClaimReview;
  onRespond: (id: number, action: "accept" | "correct" | "reject", payload?: any) => Promise<void>;
}) {
  const [mode, setMode] = useState<"idle" | "correcting" | "rejecting">("idle");
  const [text, setText] = useState(claim.correctedValue || claim.rejectReason || "");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function go(action: "accept" | "correct" | "reject", payload?: any) {
    setBusy(true);
    setLocalError(null);
    try {
      await onRespond(claim.id, action, payload);
      setMode("idle");
    } catch (e: any) {
      setLocalError(e?.message || "Update failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-5">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <FileText className="w-3.5 h-3.5 text-white/60" />
          <span className="text-xs font-mono uppercase tracking-wider text-white/60">{claim.fieldLabel}</span>
          <span className="text-[10px] text-white/60">·</span>
          <span className="text-[10px] text-white/60">{claim.sourceLabel}</span>
        </div>
        <StatusBadge status={claim.status} />
      </div>

      <p className="text-sm text-white/85 leading-relaxed mb-3">{claim.proposedValue}</p>

      {claim.status === "corrected" && claim.correctedValue && (
        <div className="mb-3 bg-[#FF5C00]/5 border border-[#FF5C00]/20 rounded-md p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#FF5C00] mb-1">Your correction</div>
          <p className="text-sm text-white/85 leading-relaxed">{claim.correctedValue}</p>
        </div>
      )}
      {claim.status === "rejected" && claim.rejectReason && (
        <div className="mb-3 bg-red-500/5 border border-red-500/20 rounded-md p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-red-400 mb-1">Reason</div>
          <p className="text-sm text-white/85 leading-relaxed">{claim.rejectReason}</p>
        </div>
      )}
      {claim.status === "admin_published" && (
        <div className="mb-3 bg-amber-500/5 border border-amber-500/20 rounded-md p-3 flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-white/75 leading-relaxed">
            Admin has published this to your investor view. You can still accept, correct, or reject it. Your response will replace the admin version.
          </div>
        </div>
      )}

      {/* Actions */}
      {mode === "idle" && (claim.status === "pending" || claim.status === "admin_published") && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => go("accept")}
            disabled={busy}
            data-testid={`button-accept-claim-${claim.id}`}
            className="inline-flex items-center gap-1.5 bg-green-500/15 hover:bg-green-500/25 border border-green-500/30 text-green-300 text-xs font-semibold rounded-md px-3 py-1.5"
          >
            <Check className="w-3.5 h-3.5" /> Accept
          </button>
          <button
            onClick={() => { setMode("correcting"); setText(claim.proposedValue); }}
            data-testid={`button-correct-claim-${claim.id}`}
            className="inline-flex items-center gap-1.5 bg-[#FF5C00]/15 hover:bg-[#FF5C00]/25 border border-[#FF5C00]/30 text-[#FF5C00] text-xs font-semibold rounded-md px-3 py-1.5"
          >
            <Edit3 className="w-3.5 h-3.5" /> Correct
          </button>
          <button
            onClick={() => { setMode("rejecting"); setText(""); }}
            data-testid={`button-reject-claim-${claim.id}`}
            className="inline-flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 text-red-300 text-xs font-semibold rounded-md px-3 py-1.5"
          >
            <X className="w-3.5 h-3.5" /> Reject
          </button>
        </div>
      )}

      {mode === "idle" && (claim.status === "accepted" || claim.status === "corrected" || claim.status === "rejected") && (
        <button
          onClick={() => { setMode("correcting"); setText(claim.correctedValue || claim.proposedValue); }}
          className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"
        >
          <RefreshCw className="w-3 h-3" /> Change my response
        </button>
      )}

      {mode === "correcting" && (
        <div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            maxLength={5000}
            data-testid={`input-correct-claim-${claim.id}`}
            className="w-full bg-black/40 border border-white/[0.12] rounded-md px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#FF5C00]/50"
            placeholder="Write the correct version"
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => go("correct", { correctedValue: text })}
              disabled={busy || !text.trim()}
              className="bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 text-white text-xs font-semibold rounded-md px-3 py-1.5"
            >
              {busy ? "Saving" : "Save correction"}
            </button>
            <button onClick={() => setMode("idle")} className="text-xs text-white/60 hover:text-white px-3 py-1.5">
              Cancel
            </button>
          </div>
        </div>
      )}

      {mode === "rejecting" && (
        <div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            maxLength={2000}
            data-testid={`input-reject-claim-${claim.id}`}
            className="w-full bg-black/40 border border-white/[0.12] rounded-md px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-red-500/40"
            placeholder="Why is this wrong"
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => go("reject", { rejectReason: text })}
              disabled={busy || !text.trim()}
              className="bg-red-500/80 hover:bg-red-500 disabled:opacity-40 text-white text-xs font-semibold rounded-md px-3 py-1.5"
            >
              {busy ? "Saving" : "Reject"}
            </button>
            <button onClick={() => setMode("idle")} className="text-xs text-white/60 hover:text-white px-3 py-1.5">
              Cancel
            </button>
          </div>
        </div>
      )}

      {localError && <p className="text-xs text-red-400 mt-2">{localError}</p>}
    </div>
  );
}

function StatusBadge({ status }: { status: ClaimStatus }) {
  const map: Record<ClaimStatus, { label: string; cls: string }> = {
    pending: { label: "Awaiting your review", cls: "bg-white/[0.06] text-white/60 border-white/[0.1]" },
    accepted: { label: "Accepted", cls: "bg-green-500/10 text-green-300 border-green-500/30" },
    corrected: { label: "Corrected", cls: "bg-[#FF5C00]/10 text-[#FF5C00] border-[#FF5C00]/30" },
    rejected: { label: "Rejected", cls: "bg-red-500/10 text-red-300 border-red-500/30" },
    admin_published: { label: "Admin published", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  };
  const x = map[status];
  return (
    <span className={`text-[10px] font-mono uppercase tracking-wider rounded px-2 py-0.5 border ${x.cls}`}>
      {x.label}
    </span>
  );
}
